import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import PageHeader from 'components/Header/Page';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import LoaderComponent from 'components/Loader';
import QueryBuilder from './QueryBuilder';
import { getComputeDetailsData, workspaceGetEnv, getWorkspaceOptions, fetchSectorList } from 'service/service';
import moment from 'moment';
import FiltersBar from './FilterBar';
import { QueryRule, FieldOption, ENV_OPTIONS_DEFAULT, formatCompactNumber } from 'utils/adb-common-util';

const KEY_TO_NAME_MAP: Record<string, string> = {
    workspace_id: 'Workspace ID',
    workspace_name: 'Workspace Name',
    workspace_url: 'Workspace URL',
    env: 'Environment',
    computetype: 'Compute Type',
    cluster_name: 'Cluster Name',
    cluster_id: 'Cluster ID',
    job_name: 'Job Name',
    usage_date: 'Usage Date',
    dbu: 'DBU',
    compute_cost: 'Compute Cost',
    compute_count: 'Compute Count',
    total_runtime_hours: 'Total Runtime Hours',
    auto_termination_minutes: 'Auto Termination (min)'
};

const WorkspaceUrlFormatter = ({ row }: any) => {
    const rawUrl = row?.workspace_url;
    const fullUrl = rawUrl && !rawUrl.startsWith('http') ? `https://${rawUrl}` : rawUrl;
    let displayText = '--';
    if (fullUrl) {
        const match = fullUrl.match(/^https?:\/\/([^.]+)/);
        displayText = match ? match[1] : fullUrl;
    }
    return (
        <div title={fullUrl}>
            {fullUrl ? (
                <a href={fullUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4fc3f7', textDecoration: 'underline' }}>
                    {displayText}
                </a>
            ) : '--'}
        </div>
    );
};

const handleDateChange = (stDt: string, edDt: string, selectedDate: string | undefined, utils: any) => {
    const { setSelectedDateInString, setStartDate, setEndDate, setPage, updateHistoryState } = utils;
    const selected = selectedDate || 'Hrs';
    setSelectedDateInString(selected);
    const yday = moment().tz('Asia/Kolkata').subtract(1, 'day');
    const end = yday.clone().endOf('day');
    let start;

    if (selected === 'Hrs') start = yday.clone().startOf('day');
    else if (selected === '7d') start = yday.clone().subtract(6, 'days').startOf('day');
    else if (selected === '30d') start = yday.clone().subtract(29, 'days').startOf('day');
    else if (selected === '1M') start = yday.clone().subtract(1, 'month').startOf('day');
    else if (selected === '3M') start = yday.clone().subtract(3, 'months').startOf('day');
    else if (selected === '6M') start = yday.clone().subtract(6, 'months').startOf('day');
    else if (selected === '1Y') start = yday.clone().subtract(1, 'year').startOf('day');
    else if (selected === 'custom') {
        start = moment(stDt);
        const customEnd = moment(edDt).endOf('day');
        const s = start.format('YYYY-MM-DD');
        const e = customEnd.format('YYYY-MM-DD');
        setStartDate(s); setEndDate(e); setPage(1);
        updateHistoryState({ startDate: s, endDate: e, selectedDateInString: selected });
        return;
    } else start = moment(stDt).startOf('day');

    const s = start.format('YYYY-MM-DD');
    const e = end.format('YYYY-MM-DD');
    setStartDate(s); setEndDate(e); setPage(1);
    updateHistoryState({ startDate: s, endDate: e, selectedDateInString: selected });
};

const getColumns = (showComputeCount: boolean) => {
    const cols: any[] = [
        { key: 'workspace_id', name: 'Workspace ID', minWidth: 180, formatter: ({ row }: any) => <div title={row?.workspace_id}>{row?.workspace_id || '--'}</div> },
        { key: 'workspace_name', name: 'Workspace Name', minWidth: 200, formatter: ({ row }: any) => <div title={row?.workspace_name}>{row?.workspace_name || '--'}</div> },
        { key: 'workspace_url', name: 'Workspace URL', minWidth: 200, formatter: WorkspaceUrlFormatter },
        { key: 'env', name: 'Environment', minWidth: 120, formatter: ({ row }: any) => <div>{row?.env?.toUpperCase() || '--'}</div> },
        { key: 'computetype', name: 'Compute Type', minWidth: 140, formatter: ({ row }: any) => <div>{row?.computetype || '--'}</div> },
        { key: 'cluster_name', name: 'Cluster Name', minWidth: 250, formatter: ({ row }: any) => <div title={row?.cluster_name}>{row?.cluster_name || '--'}</div> },
        { key: 'cluster_id', name: 'Cluster ID', minWidth: 180, formatter: ({ row }: any) => <div title={row?.cluster_id}>{row?.cluster_id || '--'}</div> },
        { key: 'job_name', name: 'Job Name', minWidth: 200, formatter: ({ row }: any) => <div title={row?.job_name}>{row?.job_name || '--'}</div> },
        { key: 'usage_date', name: 'Usage Date', minWidth: 120, formatter: ({ row }: any) => <div>{row?.usage_date ? moment.utc(row?.usage_date).format('YYYY-MM-DD') : '--'}</div> },
        {
            key: 'dbu', name: 'DBU', minWidth: 100, formatter: ({ row }: any) => {
                const v = row?.dbu;
                return <div>{typeof v === 'number' ? formatCompactNumber(v) : v || '--'}</div>;
            }
        },
    ];

    if (showComputeCount) {
        cols.push({
            key: 'compute_count', name: 'Compute Count', minWidth: 130, formatter: ({ row }: any) => {
                const v = row?.compute_count;
                return <div>{typeof v === 'number' ? `$${v.toFixed(2)}` : v || '--'}</div>;
            }
        });
    }

    cols.push({
        key: 'compute_cost', name: 'Compute Cost', minWidth: 130, formatter: ({ row }: any) => {
            const v = row?.compute_cost;
            return <div>{typeof v === 'number' ? `$${v.toFixed(2)}` : v || '--'}</div>;
        }
    });

    cols.push({
        key: 'total_runtime_hours', name: 'Total Runtime Hours', minWidth: 160, formatter: ({ row }: any) => {
            const v = row?.total_runtime_hours;
            return <div>{typeof v === 'number' ? v.toFixed(2) : v || '--'}</div>;
        }
    });

    cols.push({
        key: 'auto_termination_minutes', name: 'Auto Termination (min)', minWidth: 180, formatter: ({ row }: any) => {
            const v = row?.auto_termination_minutes;
            return <div>{v !== null && v !== undefined ? v : '--'}</div>;
        }
    });

    return cols;
};

interface ComputeDetailsTableBaseProps {
    defaultTitle: string;
    showComputeCount?: boolean;
}

const ComputeDetailsTableBase: React.FC<ComputeDetailsTableBaseProps> = ({ defaultTitle, showComputeCount = false }) => {
    const routerLocation = useLocation();
    const navigate = useNavigate();
    const navigationState = routerLocation.state;

    const {
        dateLabel = defaultTitle,
        env: navEnv,
        location: navLocation,
        computeType: navComputeType,
        parentPageTitle
    } = navigationState || {};

    const yesterday = useMemo(() => moment().tz('Asia/Kolkata').subtract(1, 'day'), []);
    const [startDate, setStartDate] = useState(navigationState?.startDate || yesterday.clone().subtract(3, 'months').startOf('day').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(navigationState?.endDate || yesterday.clone().endOf('day').format('YYYY-MM-DD'));
    const [selectedDateInString, setSelectedDateInString] = useState(navigationState?.selectedDateInString || '3M');

    const [sector, setSector] = useState<string>(navigationState?.sector || 'all');
    const [env, setEnv] = useState(navEnv || 'both');
    const [locationFilter, setLocationFilter] = useState(navLocation || 'South Central US');
    const [envOptionsState, setEnvOptionsState] = useState(ENV_OPTIONS_DEFAULT);

    const initialEnvAppliedRef = useRef(false);
    const envRef = useRef(env);
    useEffect(() => { envRef.current = env; }, [env]);

    const [tableRecords, setTableRecords] = useState<any[]>([]);
    const [loader, setLoader] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
    const [queryBuilderFilters, setQueryBuilderFilters] = useState<QueryRule[]>(() => {
        if (navComputeType && navComputeType.toLowerCase() !== 'all') {
            return [{ id: '1', operator: 'AND', field: 'Environment', value: navComputeType }];
        }
        return [];
    });
    const [workspaceOptions, setWorkspaceOptions] = useState<FieldOption[]>([{ label: 'All', value: 'all' }]);
    const [computeTypeOptions, setComputeTypeOptions] = useState<FieldOption[]>([{ label: 'All', value: 'all' }]);
    const [sortingInfo, setSortingInfo] = useState<{ sortBy: string; sortDir: 'asc' | 'desc' | '' }>({ sortBy: '', sortDir: '' });
    const [sectorList, setSectorList] = useState<string[]>([]);
    const lastFetchParamsRef = useRef<string>('');

    useEffect(() => {
        fetchSectorList({}).then(resp => { if (resp?.success) setSectorList(resp?.data || []); });
    }, []);

    const updateHistoryState = (overrides: any) => {
        const newState = { dateLabel, startDate, endDate, env, sector, location: locationFilter, selectedDateInString, computeType: navComputeType, ...overrides };
        navigate(routerLocation.pathname, { state: newState, replace: true });
    };

    const mapRuleToApiFilter = useCallback((rule: QueryRule) => {
        const rv = rule.value?.toString() ?? '';
        if (!rv || rv.toLowerCase() === 'all') return null;
        if (rule.field === 'Workspace Name') {
            const opt = workspaceOptions.find(o => o.value === rv || (o as any).workspaceId === rv || (o as any).workspaceName === rv);
            return { operator: 'AND', field: 'Workspace ID', value: (opt as any)?.workspaceId || rv };
        }
        if (rule.field === 'Environment' || rule.field === 'Compute Type') {
            return { operator: 'OR', field: 'Compute Type', value: rule.value };
        }
        return { operator: rule.operator || 'AND', field: rule.field, value: rule.value };
    }, [workspaceOptions]);

    const getApiFilters = useCallback(() => {
        const filters: any[] = [
            { operator: 'OR', field: 'Sector', value: (sector && sector !== 'all' ? sector : 'All') },
            { operator: 'OR', field: 'Environment', value: (env && env !== 'both' ? env : 'All') },
            { operator: 'AND', field: 'Location', value: (locationFilter && locationFilter !== 'ALL' ? locationFilter : 'All') }
        ];
        queryBuilderFilters.forEach(rule => {
            const f = mapRuleToApiFilter(rule);
            if (f) filters.push(f);
        });
        if (!filters.some(f => f.field === 'Compute Type')) {
            filters.push({
                operator: 'OR',
                field: 'Compute Type',
                value: (navComputeType && navComputeType.toLowerCase() !== 'all' ? navComputeType : 'All')
            });
        }
        if (!filters.some(f => f.field === 'Workspace ID')) {
            filters.push({ operator: 'AND', field: 'Workspace ID', value: 'All' });
        }
        return filters;
    }, [sector, env, locationFilter, queryBuilderFilters, navComputeType, mapRuleToApiFilter]);

    useEffect(() => {
        const getEnvFetch = async (loc: string) => {
            try {
                const res = await workspaceGetEnv(loc);
                const options = [{ label: 'ALL', value: 'both' }, ...(res?.data || [])];
                setEnvOptionsState(options);
                if (!initialEnvAppliedRef.current && navEnv) {
                    setEnv(navEnv);
                    initialEnvAppliedRef.current = true;
                } else {
                    const availableValues = options.map(o => o.value);
                    if (!availableValues.includes(envRef.current)) {
                        setEnv(availableValues.includes('both') ? 'both' : (availableValues[0] || 'both'));
                    }
                }
            } catch (error) { console.error(error); }
        };
        getEnvFetch(locationFilter);
    }, [locationFilter, navEnv]);

    useEffect(() => {
        getWorkspaceOptions(startDate, endDate, env, sector, locationFilter, selectedDateInString).then(resp => {
            if (resp?.success && resp?.data) {
                setComputeTypeOptions([{ label: 'All', value: 'all' }, ...['INTERACTIVE', 'JOB', 'SQL'].map(type => ({ label: type, value: type }))]);
                let workspaces = [];
                if (Array.isArray(resp.data.workspaceName)) {
                    workspaces = resp.data.workspaceName.slice().sort().map((name: string) => ({ label: name, value: name, workspaceName: name, workspaceId: name }));
                } else if (Array.isArray(resp.data)) {
                    workspaces = resp.data.map((w: any) => ({ label: w.workspace_name, value: w.workspace_id, workspaceName: w.workspace_name, workspaceId: w.workspace_id }))
                        .filter((w: any) => w.label).sort((a: any, b: any) => a.label.localeCompare(b.label));
                }
                setWorkspaceOptions([{ label: 'All', value: 'all' }, ...workspaces]);
            }
        });
    }, [startDate, endDate, env, sector, locationFilter, selectedDateInString]);

    const fetchData = useCallback(async () => {
        const apiFilters = getApiFilters();
        const apiSortBy = sortingInfo.sortBy ? (KEY_TO_NAME_MAP[sortingInfo.sortBy] || sortingInfo.sortBy) : undefined;
        const apiSortDir = sortingInfo.sortDir || undefined;

        const fetchParams = JSON.stringify({
            startDate, endDate, env, sector, locationFilter, page, pageSize, apiFilters, selectedDateInString, apiSortBy, apiSortDir
        });

        if (lastFetchParamsRef.current === fetchParams) return;
        lastFetchParamsRef.current = fetchParams;

        setLoader(true);
        try {
            const resp = await getComputeDetailsData({
                startDate,
                endDate,
                env,
                sector,
                location: locationFilter,
                page,
                pageSize,
                filters: apiFilters,
                selectedDateInString,
                workspaceName: undefined,
                exportType: undefined,
                sortField: apiSortBy,
                sortOrder: apiSortDir
            });

            if (resp?.success && resp?.data?.data) {
                setTableRecords(resp.data.data);
                const total = resp.data.pagination?.total || resp.data.data.length;
                const pages = resp.data.pagination?.totalPages || Math.ceil(resp.data.data.length / pageSize);
                setPaginationData({ totalElements: total, totalPages: pages });
            } else {
                setTableRecords([]);
                setPaginationData({ totalElements: 0, totalPages: 0 });
            }
        } catch (error) {
            setTableRecords([]);
            setPaginationData({ totalElements: 0, totalPages: 0 });
        } finally {
            setLoader(false);
        }
    }, [startDate, endDate, env, sector, locationFilter, selectedDateInString, page, pageSize, getApiFilters, sortingInfo]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleExportRequest = async () => {
        try {
            const apiFilters = getApiFilters();
            const apiSortBy = sortingInfo.sortBy ? (KEY_TO_NAME_MAP[sortingInfo.sortBy] || sortingInfo.sortBy) : undefined;
            await getComputeDetailsData({
                startDate,
                endDate,
                env,
                sector,
                location: locationFilter,
                page: 1,
                pageSize: 10000,
                filters: apiFilters,
                selectedDateInString,
                workspaceName: undefined,
                exportType: 'excel',
                sortField: apiSortBy,
                sortOrder: sortingInfo.sortDir || undefined
            });
        } catch (err) {
            console.error('Export error', err);
        }
    };

    const handleDateSelect = (stDt: string, edDt: string, selectedDate?: string) => {
        handleDateChange(stDt, edDt, selectedDate, {
            setSelectedDateInString,
            setStartDate,
            setEndDate,
            setPage,
            updateHistoryState
        });
    };

    return (
        <Box bgcolor="backgroundLight.primary" sx={{ mt: 10 }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2, px: 2 }}>
                <Box sx={{ mb: 2, fontSize: '1.125rem', fontWeight: '600', color: '#003771' }}>
                    <span style={{ cursor: 'pointer' }} onClick={() => navigate('/databricksADB/workspace', { state: { startDate, endDate, env, sector, location: locationFilter, selectedDateInString } })}>
                        Workspace Overview
                    </span>
                    {parentPageTitle && (
                        <>
                            {' > '}
                            <span style={{ cursor: 'pointer' }} onClick={() => navigate(-1)}>
                                {parentPageTitle}
                            </span>
                        </>
                    )}
                    {' > '}
                    <span>{dateLabel}</span>
                    {((selectedDateInString === 'customDateRange' && startDate && endDate && startDate !== endDate)) && (
                        <span style={{ marginLeft: 8, color: '#003771', fontSize: '0.95rem' }}>({`${startDate} to ${endDate}`})</span>
                    )}
                </Box>
                <PageHeader title="" icon={''} lastSync={null}>
                    <FiltersBar
                        location={locationFilter} setLocation={setLocationFilter}
                        env={env} setEnv={setEnv} envOptions={envOptionsState} setEnvOptions={setEnvOptionsState}
                        startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate}
                        handleDateSelect={handleDateSelect} selectedDateInString={selectedDateInString} setSelectedDateInString={setSelectedDateInString}
                        sectorName={sector} setSectorName={(v) => { const r = typeof v === 'function' ? (v as any)(sector) : v; setSector(r); setEnv('both'); updateHistoryState({ sector: r, env: 'both' }); }}
                        updateHistoryState={updateHistoryState} sectorOptionsList={sectorList}
                    />
                </PageHeader>
            </Stack>

            <Box sx={{ px: 2 }}>
                <QueryBuilder
                    title="Run Query" sectorOptions={workspaceOptions} environmentOptions={computeTypeOptions}
                    locationOptions={[]} teamFlagOptions={[]}
                    onRunQuery={(rules) => { setQueryBuilderFilters(rules.filter(r => r.value?.toString().toLowerCase() !== 'all')); setPage(1); }}
                    initialFilters={[
                        { id: '1', operator: 'AND', field: 'Compute Type', value: (navComputeType || 'all') },
                        { id: '2', operator: 'AND', field: 'Workspace Name', value: 'all' },
                    ]}
                />
            </Box>

            <Box sx={{ px: 2, pb: 4 }}>
                {loader ? <LoaderComponent pt={20} /> : (
                    <CustomizedDataGrid
                        title={`${defaultTitle} (${paginationData.totalElements.toLocaleString()} )`}
                        tableColumns={getColumns(showComputeCount)} tableData={tableRecords} tableType="compute-details-table"
                        defaultColumnNames={Object.values(KEY_TO_NAME_MAP).filter(n => showComputeCount || n !== 'Compute Count')}
                        page={page} pageSize={pageSize} paginationData={paginationData} listLength={tableRecords.length}
                        handlepageSizeChange={(e: any) => { setPageSize(Number(e.target.value)); setPage(1); }}
                        handlePageChange={(_: any, v: number) => setPage(v)}
                        isDateRangeFilter={false} isDisabled={false} isFetching={loader}
                        showExport={true} exportTableData={handleExportRequest}
                        onSorting={(s: any) => setSortingInfo(s?.sortBy && s.sortDir ? s : { sortBy: '', sortDir: '' })}
                        currSorting={sortingInfo} onFilterCheck={() => { }}
                    />
                )}
            </Box>
        </Box>
    );
};

export default ComputeDetailsTableBase;
